import { useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { Upload, Pencil } from "lucide-react";
import { useDatos } from "@/features/datos/store/store";
import { prepararSemilla } from "@/features/datos/persistencia/useBorrador";
import { borrarLocal } from "@/features/datos/persistencia/local";
import { useAuth } from "@/auth/AuthProvider";
import { Boton, Tarjeta, Numero, Badge } from "@/components/ui";
import { useToast } from "@/components/ui/Toast";
import { api, ApiError } from "@/lib/api";
import type { ModelInputs, Snapshot } from "@/engine/types";
import type { Nota } from "@/engine/parse/xlsx";
import { fmtMoney, fmtPct } from "@/engine/format/esMX";

type Estado =
  | { fase: "vacio" }
  | { fase: "leyendo" }
  | { fase: "error"; mensaje: string }
  | { fase: "listo"; inputs: ModelInputs; preview: Snapshot; fileName: string; bytes: number; sha256: string; sheetNames: string[]; warnings: string[]; structure: Nota[]; label: string; sinSensibles: boolean };

/** Lo que se guarda con la foto: las diferencias de estructura primero, luego los avisos de celdas. */
const avisosParaGuardar = (e: Extract<Estado, { fase: "listo" }>): string[] => [...e.structure.map((n) => n.text), ...e.warnings];

async function sha256Hex(buf: ArrayBuffer): Promise<string> {
  const h = await crypto.subtle.digest("SHA-256", buf);
  return [...new Uint8Array(h)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** Subir el MODELO WX: se lee EN EL BROWSER (solo celdas azules); al servidor solo viajan esas celdas. */
export function Subir() {
  const [estado, setEstado] = useState<Estado>({ fase: "vacio" });
  const [guardando, setGuardando] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const qc = useQueryClient();
  const [, navegar] = useLocation();
  const { member, sensibles } = useAuth();
  const seed = useDatos((x) => x.seed);

  function editarAqui() {
    if (estado.fase !== "listo") return;
    const { inputs, avisos } = prepararSemilla(estado.inputs);
    seed(inputs, { modo: "real", source: "excel", seedSnapshotId: null, seedUpload: { fileName: estado.fileName, sha256: estado.sha256, bytes: estado.bytes, sheetNames: estado.sheetNames, warnings: avisosParaGuardar(estado) }, revisionLocal: 1, revisionGuardada: 0, guardado: "pendiente", updatedAt: new Date().toISOString(), avisos });
    toast.ok("Listo: tus datos del Excel quedaron en Mis datos. Corrige lo que falte y guarda.");
    navegar("/datos");
  }

  async function leer(file: File) {
    setEstado({ fase: "leyendo" });
    try {
      const buf = await file.arrayBuffer();
      const [{ parseWorkbook }, { compute }, { emptyInputs }] = await Promise.all([import("@/engine/parse/xlsx"), import("@/engine"), import("@/engine/versions")]);
      const r = parseWorkbook(buf);
      if (!r.inputs) {
        setEstado({ fase: "error", mensaje: `No reconocí este archivo como MODELO WX FINAL2. Faltan pestañas: ${r.missingSheets.join(", ") || "ninguna"}. Si traes la plantilla vieja (11 pestañas), baja la nueva y copia tus celdas azules.` });
        return;
      }
      // Sin autorización de datos sensibles, el diagnóstico y Mi identidad del Excel no se cargan (aviso de privacidad, casilla C)
      const vacio = emptyInputs(r.inputs.version);
      const inputs = sensibles ? r.inputs : { ...r.inputs, diag: vacio.diag, identity: vacio.identity };
      const preview = compute(inputs);
      setEstado({ fase: "listo", inputs, preview, fileName: file.name, bytes: file.size, sha256: await sha256Hex(buf), sheetNames: r.sheetNames, warnings: r.warnings, structure: r.structure, label: "Entrada", sinSensibles: !sensibles });
    } catch (e) {
      setEstado({ fase: "error", mensaje: `No pude leer el archivo: ${(e as Error).message}` });
    }
  }

  // Si no se pudo ubicar una parte del Excel, no se guarda a ciegas: el miembro revisa en Mis datos.
  const hayDuda = estado.fase === "listo" && estado.structure.some((n) => n.level === "duda");

  async function guardar() {
    if (estado.fase !== "listo" || hayDuda) return;
    setGuardando(true);
    try {
      const r = await api<{ snapshotId: string }>("/api/snapshots", {
        method: "POST",
        json: { modelVersion: estado.inputs.version, inputs: estado.inputs, label: estado.label, source: "excel", upload: { fileName: estado.fileName, sha256: estado.sha256, bytes: estado.bytes, sheetNames: estado.sheetNames, warnings: avisosParaGuardar(estado) } },
      });
      // Mis datos queda con lo del Excel ya acomodado (la sesión ya traía un borrador vacío en memoria).
      const { inputs, avisos } = prepararSemilla(estado.inputs);
      seed(inputs, { modo: "real", source: "snapshot", seedSnapshotId: r.snapshotId, seedUpload: null, revisionLocal: 0, revisionGuardada: 0, guardado: "guardado", lastSavedAt: new Date().toISOString(), updatedAt: new Date().toISOString(), avisos });
      if (member) borrarLocal(member.id);
      try { await api("/api/drafts", { method: "PUT", json: { modelVersion: inputs.version, source: "snapshot", seedSnapshotId: r.snapshotId, inputs } }); } catch { /* el borrador se rehace al editar */ }
      await qc.invalidateQueries({ queryKey: ["snapshot"] });
      await qc.invalidateQueries({ queryKey: ["draft"] });
      toast.ok("Listo: tus números del Excel quedaron acomodados en la app.");
      navegar("/foto");
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "No pude guardar. Intenta de nuevo.");
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-2xl font-semibold">Subir mi Excel</h1>
        <p className="mt-1 text-tinta-suave">El MODELO WX (.xlsx) que llenaste en Wealth X. La app toma todos tus números y los acomoda en Mis datos, Mi foto y el asesor. Se lee aquí, en tu dispositivo: al servidor solo viajan las celdas que tú llenaste, nunca el archivo.</p>
      </div>
      <input ref={inputRef} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void leer(f); }} />
      {estado.fase !== "listo" && (
        <button type="button" onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) void leer(f); }}
          className="flex flex-col items-center gap-2 rounded-md border-2 border-dashed border-borde bg-white p-10 text-tinta-suave hover:border-primary">
          <Upload className="h-8 w-8" aria-hidden />
          <span className="font-medium text-tinta">{estado.fase === "leyendo" ? "Leyendo…" : "Elegir archivo o arrastrarlo aquí"}</span>
          <span className="text-sm">No hace falta guardarlo con ⌘S antes: el motor recalcula todo.</span>
        </button>
      )}
      {estado.fase === "error" && <div className="rounded-md border border-red-200 bg-error-suave p-4 text-red-900">{estado.mensaje}</div>}
      {estado.fase === "listo" && (
        <>
          <Tarjeta titulo="Lo que leí" accion={<Badge tono="ok">{estado.preview.meta.modelVersion}</Badge>}>
            <p className="text-sm text-tinta-suave">{estado.fileName} · etapa: <b>{estado.preview.completeness.stage}</b> · {estado.preview.flags.length} observaciones del motor</p>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <Numero etiqueta="Patrimonio neto" valor={fmtMoney(estado.preview.photo.netWorth, estado.preview.setup.currency)} />
              <Numero etiqueta="Tasa de ahorro" valor={fmtPct(estado.preview.photo.savingsRate)} />
              <Numero etiqueta="% libertad financiera" valor={fmtPct(estado.preview.photo.freedomPct)} />
              <Numero etiqueta="Rend. solo invertible" valor={fmtPct(estado.preview.derived.investableReturn)} nota={`el modelo muestra ${fmtPct(estado.preview.photo.expectedReturn)}`} />
            </div>
            {estado.sinSensibles && <p className="mt-3 text-sm text-tinta-suave">No cargué tu diagnóstico ni Mi identidad porque no autorizaste el uso de datos sensibles. Si quieres incluirlos, autorízalo en Mis datos › Diagnóstico y vuelve a subir el archivo.</p>}
            {estado.structure.length > 0 && (
              <ul className="mt-3 flex flex-col gap-2 text-sm">
                {estado.structure.map((n, i) => (
                  <li key={i} className={n.level === "duda" ? "rounded-md border border-red-200 bg-error-suave p-3 text-red-900" : "rounded-md border border-borde bg-fondo p-3 text-tinta"}>{n.text}</li>
                ))}
              </ul>
            )}
            {estado.warnings.length > 0 && (
              <ul className="mt-3 list-disc pl-5 text-sm text-amber-900">
                {estado.warnings.slice(0, 5).map((w, i) => <li key={i}>{w}</li>)}
                {estado.warnings.length > 5 && <li className="list-none text-tinta-suave">…y {estado.warnings.length - 5} avisos más.</li>}
              </ul>
            )}
          </Tarjeta>
          {hayDuda && <p className="text-sm text-red-900">Para guardar, primero revisa tus datos aquí: no pude ubicar una parte de tu Excel.</p>}
          <div className="flex flex-col gap-3 sm:flex-row">
            <Boton tamano="lg" className="flex-1" cargando={guardando} disabled={hayDuda} onClick={guardar}>Guardar y ver mi foto</Boton>
            <Boton variante="secundario" tamano="lg" className="flex-1" onClick={editarAqui}><Pencil className="h-4 w-4" aria-hidden /> Revisar y editar aquí</Boton>
          </div>
          <div>
            <Boton variante="fantasma" onClick={() => setEstado({ fase: "vacio" })}>Elegir otro archivo</Boton>
          </div>
        </>
      )}
      <p className="text-sm text-tinta-suave">¿No tienes tu Excel a la mano? <Link href="/datos" className="font-medium text-primary underline">Empieza desde cero aquí</Link>.</p>
    </div>
  );
}
